<script setup lang="ts">
import type { CSSProperties } from 'vue'

const props = withDefaults(defineProps<{
  modelValue: string | null
  placeholder?: string
  editable?: boolean
  tag?: string
  displayStyle?: CSSProperties
}>(), {
  placeholder: 'Click to edit',
  editable: false,
  tag: 'span',
  displayStyle: () => ({})
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const isEditing = ref(false)
const editValue = ref('')
const inputRef = ref<HTMLInputElement | null>(null)

function startEditing() {
  if (!props.editable) return
  editValue.value = props.modelValue ?? ''
  isEditing.value = true
  nextTick(() => {
    inputRef.value?.focus()
    inputRef.value?.select()
  })
}

function save() {
  isEditing.value = false
  const trimmed = editValue.value.trim()
  if (trimmed !== (props.modelValue ?? '')) {
    emit('update:modelValue', trimmed)
  }
}

function cancel() {
  isEditing.value = false
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter') {
    e.preventDefault()
    save()
  } else if (e.key === 'Escape') {
    e.preventDefault()
    cancel()
  }
}
</script>

<template>
  <div class="group relative inline-block">
    <input
      v-if="isEditing"
      ref="inputRef"
      v-model="editValue"
      type="text"
      class="w-full bg-transparent border-0 border-b-2 border-primary-400/40 outline-none p-0"
      :style="{ ...displayStyle, textAlign: 'inherit' }"
      @blur="save"
      @keydown="onKeydown"
    >
    <component
      :is="tag"
      v-else
      :class="[
        editable && 'cursor-text rounded-lg transition-colors duration-200 hover:bg-white/5 -mx-3 px-3 -my-1 py-1'
      ]"
      :style="displayStyle"
      @click="startEditing"
    >
      {{ modelValue || (editable ? placeholder : '') }}
      <span
        v-if="editable"
        class="ml-2 opacity-30 group-hover:opacity-60 transition-opacity duration-200 align-middle inline-block"
      >
        <UIcon
          name="i-lucide-pencil"
          class="size-3.5"
        />
      </span>
    </component>
  </div>
</template>
